import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import axios from '../api/axios';

const NotificationContext = createContext();

// Notification sounds
const SOUNDS = {
    new_order: '/sounds/new-order.mp3',
    order_ready: '/sounds/order-ready.mp3',
    low_stock: '/sounds/alert.mp3',
    default: '/sounds/notification.mp3'
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [settings, setSettings] = useState({
        soundEnabled: true,
        desktopEnabled: true,
        pollInterval: 30000 // 30 seconds
    });

    const audioRef = useRef(null);
    const pollIntervalRef = useRef(null);

    // Load notifications from API
    const loadNotifications = useCallback(async () => {
        if (!localStorage.getItem('token')) return; // Avoid 401 on login page

        try {
            const response = await axios.get('/vendor/notifications');
            const data = response.data.data || response.data;

            if (Array.isArray(data)) {
                setNotifications(data);
                setUnreadCount(data.filter((n) => !n.is_read).length);
            }
        } catch (error) {
            console.error('Failed to load notifications:', error);
        }
    }, []);

    // Play notification sound
    const playSound = useCallback(
        (type = 'default') => {
            if (!settings.soundEnabled) return;

            try {
                const soundUrl = SOUNDS[type] || SOUNDS.default;
                const audio = new Audio(soundUrl);
                audio.volume = 0.7;
                audio.play().catch((e) => console.log('Audio play failed:', e));
            } catch (error) {
                console.log('Sound play error:', error);
            }
        },
        [settings.soundEnabled]
    );

    // Show desktop notification
    const showDesktopNotification = useCallback(
        (title, body, options = {}) => {
            if (!settings.desktopEnabled) return;

            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(title, {
                    body,
                    icon: '/logo192.png',
                    badge: '/logo192.png',
                    tag: options.tag || 'qr-menu-notification',
                    requireInteraction: options.requireInteraction || false,
                    ...options
                });
            }
        },
        [settings.desktopEnabled]
    );

    // Request notification permission
    const requestPermission = useCallback(async () => {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return false;
    }, []);

    // Add a new notification
    const addNotification = useCallback(
        (notification) => {
            setNotifications((prev) => [notification, ...prev]);
            setUnreadCount((prev) => prev + 1);

            // Show toast notification
            const toastStyle = {
                new_order: { icon: '🛎️', duration: 8000 },
                order_status: { icon: '📋', duration: 5000 },
                low_stock: { icon: '⚠️', duration: 6000 },
                expiry_warning: { icon: '⏰', duration: 6000 },
                payment_received: { icon: '💰', duration: 5000 },
                system: { icon: 'ℹ️', duration: 4000 }
            };

            const style = toastStyle[notification.type] || toastStyle.system;

            toast(notification.message, {
                icon: style.icon,
                duration: style.duration,
                style: {
                    direction: 'rtl',
                    fontFamily: 'Cairo, sans-serif'
                }
            });

            // Play sound
            playSound(notification.type);

            // Show desktop notification
            showDesktopNotification(notification.title, notification.message, {
                tag: notification.id
            });
        },
        [playSound, showDesktopNotification]
    );

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId) => {
        try {
            await axios.post(`/vendor/notifications/${notificationId}/read`);
            setNotifications((prev) =>
                prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    }, []);

    // Mark all as read
    const markAllAsRead = useCallback(async () => {
        try {
            await axios.post('/vendor/notifications/read-all');
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    }, []);

    // Clear all notifications
    const clearAll = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
    }, []);

    // Update settings
    const updateSettings = useCallback((newSettings) => {
        setSettings((prev) => ({ ...prev, ...newSettings }));
    }, []);

    // Initialize WebSocket/Polling
    useEffect(() => {
        if (!localStorage.getItem('token')) return; // Do not initialize if unauthenticated

        // Request notification permission on mount
        requestPermission();

        // Initial load
        loadNotifications();

        // Set up polling (fallback for WebSocket)
        pollIntervalRef.current = setInterval(loadNotifications, settings.pollInterval);

        // Try to establish WebSocket connection (if available)
        const connectWebSocket = () => {
            try {
                // Using Laravel Echo with Pusher/Soketi
                if (window.Echo) {
                    const restaurantId = localStorage.getItem('restaurant_id');
                    if (restaurantId) {
                        window.Echo.private(`restaurant.${restaurantId}`)
                            .listen('.new-order', (e) => {
                                addNotification({
                                    id: Date.now(),
                                    type: 'new_order',
                                    title: 'طلب جديد! 🎉',
                                    message: `تم استلام طلب جديد رقم #${e.order.order_number || e.order.id}`,
                                    is_read: false,
                                    data: e.order
                                });
                            })
                            .listen('.order-status-updated', (e) => {
                                addNotification({
                                    id: Date.now(),
                                    type: 'order_status',
                                    title: 'تحديث حالة الطلب',
                                    message: `تم تحديث الطلب #${e.order.order_number || e.order.id} إلى: ${e.new_status}`,
                                    is_read: false,
                                    data: e
                                });
                            })
                            .listen('.low-stock-alert', (e) => {
                                addNotification({
                                    id: Date.now(),
                                    type: 'low_stock',
                                    title: 'تنبيه انخفاض المخزون!',
                                    message: e.message,
                                    is_read: false,
                                    data: e.item
                                });
                            });

                        setIsConnected(true);
                    }
                }
            } catch (error) {
                console.log('WebSocket connection failed, using polling:', error);
                setIsConnected(false);
            }
        };

        connectWebSocket();

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [loadNotifications, addNotification, requestPermission, settings.pollInterval]);

    const value = {
        notifications,
        unreadCount,
        isConnected,
        settings,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearAll,
        updateSettings,
        loadNotifications,
        requestPermission
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {/* Hidden audio element for sounds */}
            <audio ref={audioRef} style={{ display: 'none' }} />
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

export default NotificationContext;
